/**
 * CustomerAfterburnSettings - Admin component for individual afterburn settings per customer
 * Allows admins to set custom afterburn percentages for each training type
 * Includes AI calculation feature for personalized recommendations
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Save, Loader2, Sparkles, RotateCcw, Zap, Info,
  Check, X, Brain, Flame
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerAfterburnSettings = ({ customerId, customerName, onClose, onSave }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculatingAI, setCalculatingAI] = useState({});
  const [editedSettings, setEditedSettings] = useState({});
  const [aiEnabled, setAiEnabled] = useState(true);
  
  // Training Calorie Multiplier State
  const [trainingMultiplier, setTrainingMultiplier] = useState(1.0);
  const [savingMultiplier, setSavingMultiplier] = useState(false);

  useEffect(() => {
    if (customerId) {
      loadSettings();
      loadTrainingMultiplier();
    }
  }, [customerId]);

  const loadTrainingMultiplier = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/portal/nutrition/customer/${customerId}/training-multiplier`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrainingMultiplier(response.data.training_calorie_multiplier || 1.0);
    } catch (error) {
      console.log('Using default multiplier');
      setTrainingMultiplier(1.0);
    }
  };

  const saveTrainingMultiplier = async () => {
    setSavingMultiplier(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/portal/nutrition/customer/${customerId}/training-multiplier`,
        { multiplier: trainingMultiplier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Trainings-Multiplikator auf ${trainingMultiplier}x gesetzt`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingMultiplier(false);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/nutrition/customer/${customerId}/afterburn-settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
      setAiEnabled(response.data.afterburn_ai_enabled);
      
      // Initialize edited settings (both % and kcal)
      const initialEdited = {};
      response.data.training_settings.forEach(ts => {
        if (ts.individual_percent !== null) {
          initialEdited[ts.key] = ts.individual_percent;
        }
        if (ts.individual_kcal !== undefined && ts.individual_kcal !== null) {
          initialEdited[ts.key + '_kcal'] = ts.individual_kcal;
        }
      });
      setEditedSettings(initialEdited);
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/nutrition/customer/${customerId}/afterburn-settings`,
        {
          individual_afterburn_settings: editedSettings,
          afterburn_ai_enabled: aiEnabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Nachbrenn-Einstellungen gespeichert');
      if (onSave) onSave();
      loadSettings(); // Reload to show updated values
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const calculateWithAI = async (trainingType) => {
    setCalculatingAI(prev => ({ ...prev, [trainingType]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/nutrition/customer/${customerId}/afterburn-ai-calculate?training_type=${trainingType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      setEditedSettings(prev => ({
        ...prev,
        [trainingType]: result.ai_calculated_percent
      }));
      
      toast.success(
        <div>
          <p className="font-medium">KI-Empfehlung: {result.ai_calculated_percent}%</p>
          <p className="text-sm text-gray-600">{result.reasoning}</p>
        </div>,
        { duration: 5000 }
      );
    } catch (error) {
      toast.error('KI-Berechnung fehlgeschlagen');
    } finally {
      setCalculatingAI(prev => ({ ...prev, [trainingType]: false }));
    }
  };

  const resetToDefault = (trainingType) => {
    setEditedSettings(prev => {
      const newSettings = { ...prev };
      delete newSettings[trainingType];
      return newSettings;
    });
  };

  const updateSetting = (trainingType, value) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      setEditedSettings(prev => {
        const newSettings = { ...prev };
        delete newSettings[trainingType];
        return newSettings;
      });
    } else {
      setEditedSettings(prev => ({
        ...prev,
        [trainingType]: Math.max(0, Math.min(50, numValue))
      }));
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          Keine Daten gefunden
        </CardContent>
      </Card>
    );
  }

  const hasChanges = Object.keys(editedSettings).length > 0 || 
    settings.training_settings.some(ts => 
      ts.is_customized && editedSettings[ts.key] === undefined
    );

  return (
    <div className="space-y-4">
      {/* Training Calorie Multiplier Section */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Flame className="w-5 h-5 text-blue-600" />
            Trainings-Kalorienverbrauch
          </CardTitle>
          <CardDescription>
            Individueller Multiplikator für {customerName || 'diesen Kunden'} - beeinflusst alle Trainings-Kalorien
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-sm text-gray-600 mb-1 block">Kalorienverbrauch-Faktor</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={trainingMultiplier}
                  onChange={(e) => setTrainingMultiplier(parseFloat(e.target.value))}
                  className="flex-1"
                />
                <div className="w-20">
                  <Input
                    type="number"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={trainingMultiplier}
                    onChange={(e) => setTrainingMultiplier(Math.max(0.5, Math.min(2.0, parseFloat(e.target.value) || 1.0)))}
                    className="text-center font-semibold"
                  />
                </div>
                <span className="text-lg font-bold text-blue-600">×</span>
              </div>
            </div>
          </div>
          
          {/* Multiplier Preview */}
          <div className="p-3 bg-white rounded-lg border">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Beispiel: EMS Training (30 Min)</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 line-through">450 kcal</span>
                <span className="text-blue-600 font-semibold">→ {Math.round(450 * trainingMultiplier)} kcal</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {trainingMultiplier < 0.8 && "⬇️ Niedriger Verbrauch (z.B. Anfänger, wenig Muskelmasse)"}
              {trainingMultiplier >= 0.8 && trainingMultiplier < 1.0 && "↘️ Leicht unterdurchschnittlicher Verbrauch"}
              {trainingMultiplier === 1.0 && "➡️ Durchschnittlicher Verbrauch (Standard)"}
              {trainingMultiplier > 1.0 && trainingMultiplier <= 1.2 && "↗️ Leicht überdurchschnittlicher Verbrauch"}
              {trainingMultiplier > 1.2 && trainingMultiplier <= 1.5 && "⬆️ Hoher Verbrauch (z.B. viel Muskelmasse, hohe Intensität)"}
              {trainingMultiplier > 1.5 && "🔥 Sehr hoher Verbrauch (z.B. Leistungssportler)"}
            </p>
          </div>
          
          <div className="flex justify-end">
            <Button 
              onClick={saveTrainingMultiplier} 
              disabled={savingMultiplier}
              className="gap-2"
            >
              {savingMultiplier ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Multiplikator speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            Nachbrenn-Einstellungen
          </h3>
          <p className="text-sm text-gray-500">
            Individuelle EPOC-Prozentsätze für {customerName || 'diesen Kunden'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={aiEnabled}
            onCheckedChange={setAiEnabled}
            id="ai-enabled"
          />
          <Label htmlFor="ai-enabled" className="text-sm">
            KI-Berechnung erlauben
          </Label>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-3 bg-orange-50 rounded-lg flex items-start gap-2 border border-orange-200">
        <Info className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-orange-700">
          <strong>Nachbrenn-Effekt (EPOC):</strong> Der Prozentsatz der verbrannten Kalorien, 
          die am nächsten Tag als zusätzlicher Energieverbrauch angezeigt werden. 
          Beispiel: 500 kcal Training × 20% = 100 kcal Nachbrenn am Folgetag.
        </p>
      </div>

      {/* Training Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {settings.training_settings.map((training) => {
          const isCustomized = editedSettings[training.key] !== undefined;
          const currentValue = editedSettings[training.key] ?? training.global_default_percent;
          const wasCustomized = training.is_customized && editedSettings[training.key] === undefined;
          
          return (
            <Card 
              key={training.key} 
              className={`transition-all ${
                isCustomized ? 'border-orange-300 bg-orange-50/30' : 
                wasCustomized ? 'border-red-200 bg-red-50/20' : ''
              }`}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{training.emoji}</span>
                    <span className="font-medium text-sm">{training.label}</span>
                  </div>
                  {isCustomized && (
                    <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 border-orange-300">
                      Angepasst
                    </Badge>
                  )}
                  {wasCustomized && (
                    <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300">
                      Wird zurückgesetzt
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={currentValue}
                        onChange={(e) => updateSetting(training.key, e.target.value)}
                        className="w-20 text-center font-medium"
                        min="0"
                        max="50"
                      />
                      <span className="text-sm text-gray-500">%</span>
                      <span className="text-xs text-gray-400">oder</span>
                      <Input
                        type="number"
                        placeholder="kcal"
                        value={editedSettings[training.key + '_kcal'] ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEditedSettings(prev => ({
                            ...prev,
                            [training.key + '_kcal']: val ? parseInt(val) : undefined
                          }));
                        }}
                        className="w-20 text-center font-medium"
                        min="0"
                        max="500"
                      />
                      <span className="text-sm text-gray-500">kcal</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Standard: {training.global_default_percent}% {editedSettings[training.key + '_kcal'] ? `(fix: ${editedSettings[training.key + '_kcal']} kcal)` : ''}
                    </p>
                  </div>
                  
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => calculateWithAI(training.key)}
                      disabled={calculatingAI[training.key]}
                      className="h-8 px-2"
                      title="KI-Berechnung"
                    >
                      {calculatingAI[training.key] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Brain className="w-4 h-4 text-purple-600" />
                      )}
                    </Button>
                    {isCustomized && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => resetToDefault(training.key)}
                        className="h-8 px-2"
                        title="Auf Standard zurücksetzen"
                      >
                        <RotateCcw className="w-4 h-4 text-gray-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Action Buttons */}
      <Separator />
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {Object.keys(editedSettings).length} von {settings.training_settings.length} angepasst
        </div>
        <div className="flex gap-2">
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
          )}
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="gap-2 bg-orange-500 hover:bg-orange-600"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerAfterburnSettings;
